import { GROUND_HEIGHT, LEVEL_BACKGROUNDS, backgroundImageCache, loadBackgroundImage } from './constants';
import { drawGround } from './groundThemes';
import { getLevelGradient } from './levelGradients';

// Every level 1-20 has a dedicated AI-generated sky image (Gemini Nano Banana)
// in /backgrounds/level_{N}.jpg. Gradient palette in levelGradients.js is still
// used as a fallback while images load or if a fetch fails.
function pickBgImageLevel(level) {
  if (level >= 1 && level <= 20) return level;
  // Loop for hypothetical levels beyond 20 (endless mode etc.)
  if (level > 20) return ((level - 1) % 20) + 1;
  return 1;
}

export const drawBackground = (ctx, cameraX, canvasWidth, canvasHeight, level) => {
  const bgLevel = pickBgImageLevel(level);
  const bgData = bgLevel !== null ? loadBackgroundImage(bgLevel) : null;

  if (bgData && bgData.loaded && bgData.img) {
    const img = bgData.img;
    const imgWidth = img.width;
    const imgHeight = img.height;
    const skyHeight = canvasHeight - GROUND_HEIGHT;
    const scale = skyHeight / imgHeight;
    const scaledWidth = imgWidth * scale;
    const parallaxSpeed = 0.5;
    const offset = (cameraX * parallaxSpeed) % scaledWidth;
    const numCopies = Math.ceil(canvasWidth / scaledWidth) + 2;
    for (let i = -1; i < numCopies; i++) {
      const x = i * scaledWidth - offset;
      ctx.drawImage(img, x, 0, scaledWidth, skyHeight);
    }
  } else {
    // Per-level gradient fallback (also used while images load, and always
    // for levels 11-20 which have no dedicated sky image).
    const palette = getLevelGradient(level);
    const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
    gradient.addColorStop(0,   palette.sky[0]);
    gradient.addColorStop(0.5, palette.sky[1]);
    gradient.addColorStop(1,   palette.sky[2]);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Star / dust field
    ctx.fillStyle = '#fff';
    for (let i = 0; i < 50; i++) {
      const x = ((i * 73) % canvasWidth + cameraX * 0.05) % canvasWidth;
      const y = (i * 37) % (canvasHeight - GROUND_HEIGHT - 50);
      ctx.globalAlpha = 0.3 + (i % 3) * 0.2;
      ctx.fillRect(x, y, 2, 2);
    }
    ctx.globalAlpha = 1;

    // Building silhouette with per-level color + accent windows
    ctx.fillStyle = palette.building;
    const cityOffset = cameraX * 0.2;
    for (let i = 0; i < 20; i++) {
      const x = (i * 100 - cityOffset) % (canvasWidth + 200) - 100;
      const height = 50 + (i * 47) % 150;
      ctx.fillRect(x, canvasHeight - GROUND_HEIGHT - height, 80, height);
      ctx.fillStyle = palette.accent;
      for (let j = 0; j < height / 20; j++) {
        if ((i + j) % 3 === 0) {
          ctx.fillRect(x + 10 + (j % 3) * 25, canvasHeight - GROUND_HEIGHT - height + 10 + j * 20, 15, 10);
        }
      }
      ctx.fillStyle = palette.building;
    }
  }
  
  // Draw themed ground for the current level
  drawGround(ctx, cameraX, canvasWidth, canvasHeight, level);
  
  // Ground reflection (subtle)
  const roadY = canvasHeight - GROUND_HEIGHT;
  const bgData2 = bgLevel !== null ? loadBackgroundImage(bgLevel) : null;
  if (bgData2 && bgData2.loaded && bgData2.img) {
    const img = bgData2.img;
    const imgWidth = img.width;
    const imgHeight = img.height;
    const skyHeight = canvasHeight - GROUND_HEIGHT;
    const scale = skyHeight / imgHeight;
    const scaledWidth = imgWidth * scale;
    const parallaxSpeed = 0.5;
    const offset = (cameraX * parallaxSpeed) % scaledWidth;
    const numCopies = Math.ceil(canvasWidth / scaledWidth) + 2;
    
    const reflectionHeight = 50;
    const reflectionY = roadY + 10;
    
    ctx.save();
    ctx.globalAlpha = 0.15;
    ctx.globalCompositeOperation = 'lighter';
    
    for (let i = -1; i < numCopies; i++) {
      const x = i * scaledWidth - offset;
      ctx.save();
      ctx.translate(x, reflectionY + reflectionHeight);
      ctx.scale(1, -1);
      const sourceBottomPortion = imgHeight * 0.3;
      const sourceY = imgHeight - sourceBottomPortion;
      ctx.drawImage(img, 0, sourceY, imgWidth, sourceBottomPortion, 0, 0, scaledWidth, reflectionHeight);
      ctx.restore();
    }
    
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    
    const fadeGradient = ctx.createLinearGradient(0, reflectionY, 0, reflectionY + reflectionHeight);
    fadeGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    fadeGradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.3)');
    fadeGradient.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
    ctx.fillStyle = fadeGradient;
    ctx.fillRect(0, reflectionY, canvasWidth, reflectionHeight);
    
    ctx.restore();
  }
};

export default drawBackground;
